import { useEffect, useState } from "react";
import { clone, filter, map } from "lodash";
import { useTranslation } from "react-i18next";

import { QRProps } from "@/contexts/QREditorContext/types";
import { FileProps } from "@/components/FilesUpload/types";
import { usePreviewData } from "@/features/previews/shared/hooks/usePreviewData";
import { useToast } from "@/hooks/ui/useToast";
import { isFileAblob } from "@/utils/qrEditorHelper";
import { InvalidVideoFormatToast, InvalidProviderToast } from "@/utils/uploadHelper";
import { NAMESPACES } from "@/internals/constants/namespaces";

import { UseVideoDataResult, VideoItem } from "../types";

const buildVideoItems = (videoUrls?: FileProps[]): VideoItem[] =>
  map(videoUrls, (videoData) => ({ ...videoData, isLoaded: false }));

export const useVideoData = (props: QRProps<"video">): UseVideoDataResult => {
  const { data, isInitPreview, context } = usePreviewData(props);
  const { toast } = useToast();
  const { t: tValidations } = useTranslation(NAMESPACES.VALIDATIONS);

  const [videos, setVideos] = useState<VideoItem[]>(() => buildVideoItems(data?.videoUrls));

  useEffect(() => {
    if (data?.videoUrls) {
      setVideos(buildVideoItems(data.videoUrls));
    }
  }, [data?.videoUrls]);

  const removeVideoIndex = (videoIndex: number) => {
    setVideos((prev) => filter(prev, (_, index) => videoIndex !== index));

    if (context) {
      context.setQrData({
        ...context.qrData,
        data: {
          ...context.qrData.data,
          videoUrls: filter(context.qrData.data?.videoUrls, (_, index) => videoIndex !== index),
        },
      });
    }
  };

  const handleVideoReady = (index: number, isLoaded?: boolean) => {
    setVideos((prev) => {
      const next = clone(prev);
      next[index] = { ...prev[index], isLoaded: Boolean(isLoaded) };
      return next;
    });
  };

  const handleVideoError = (index: number, video: VideoItem, error?: unknown) => {
    // A playback error after the video already loaded is non-fatal: keep it.
    if (videos[index]?.isLoaded) {
      console.warn("Non-fatal video playback error:", error);
      return;
    }

    const isBlob = isFileAblob(video.url);
    const urlStr = typeof video.url === "string" ? video.url : "";
    const isMovOrMp4 =
      video.name?.toLowerCase().endsWith(".mov") ||
      urlStr.toLowerCase().endsWith(".mov") ||
      video.name?.toLowerCase().endsWith(".mp4") ||
      urlStr.toLowerCase().endsWith(".mp4");

    // Supported uploaded formats can momentarily error while buffering: keep them.
    if (!isBlob && isMovOrMp4) {
      return;
    }

    const toastContent = isBlob
      ? InvalidVideoFormatToast(tValidations)
      : InvalidProviderToast(tValidations);

    toast({
      type: "custom",
      content: () => toastContent,
      marginBottom: 80,
      duration: 10000,
    });

    removeVideoIndex(index);
  };

  return { data, isInitPreview, videos, handleVideoReady, handleVideoError };
};
